import { useCallback, useEffect, useState, useRef } from 'react';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { inspectionTemplateClient } from 'shared/domain/inspectionTemplate/inspectionTemplateClient';
import { showChannelRequestErrorToast } from 'services/toaster';
import { getLastSelectedTemplateId } from './localStorage';
import { findLastClickedItem } from './scroll';

interface LastSelectedProps {
  templates: InspectionTemplateItemModel[];
  projectId: string;
}

interface LastSelectedResult {
  lastSelected: InspectionTemplateItemModel | undefined;
  loading: boolean;
}
export function useLastSelectedInspectionTemplate({
  templates,
  projectId,
}: LastSelectedProps): LastSelectedResult {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [lastSelected, setLastSelected] = useState<
    InspectionTemplateItemModel | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastSelectedDto, setLastSelectedDto] = useState<
    InspectionTemplateModel | undefined
  >(undefined);

  const setTemplate = useCallback((template: InspectionTemplateModel) => {
    setLastSelectedDto(template);
  }, []);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const id = getLastSelectedTemplateId(projectId);
    if (!id || id === 'undefined') {
      setLoading(false);
      return;
    }

    const lastPreviewedItem = findLastClickedItem(id, templates);
    if (lastPreviewedItem) {
      setLastSelected(lastPreviewedItem);
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    inspectionTemplateClient
      .getOneById(id)
      .then((templateModel) => {
        if (controller.signal.aborted) return;
        setTemplate(templateModel);
        setLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        showChannelRequestErrorToast('Error while getting inspection template', error);
      });
  }, [
    templates,
    projectId,
    setTemplate,
  ]);

  useEffect(() => {
    if (lastSelectedDto) {
      const id = lastSelectedDto._id;
      const lastClickedItem = findLastClickedItem(id, templates);
      if (lastClickedItem) {
        setLastSelected(lastClickedItem);
        setLoading(false);
      }
    }
  }, [lastSelectedDto, templates]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { lastSelected, loading };
}
